import { useContext, useState, useEffect, useMemo } from "react";

import Logic from "./logic";
import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";

import throwMessage from "../../utils/throwMessage";

import NewEntry from "../../components/modals/new-entry";
import NewForecast from "../../components/modals/new-forecast";
import Backtests from "../../components/modals/backtests";
import Journal from "../../components/modals/journal";
import ConfirmDialog from "../../components/modals/confirm-dialog";
// import PosCal from "../../components/modals/cal";
import EquityCurve from "../../components/equityCurve";
import FloatingSearch from "../../components/FloatingSearch";

import columnsSimple from "../../lib/columnsSimple";
import columnsEdit from "../../lib/columnsEdit";
import columnsAll from "../../lib/columnsAll";

import moment from "moment";

import UnarchivedTestsDropDown from "../../components/select/unarchivedTestsDropDown";
import CustomDataGrid from "../../components/dataGrid/index";
import ArchivedTestsDropDown from "../../components/select/archivedTestsDropDown";
import StatsBar from "../../components/statsbar";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";

import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";

import BarChartIcon from "@mui/icons-material/BarChart";
import UnarchiveIcon from "@mui/icons-material/Unarchive";

import CandlestickChartOutlinedIcon from "@mui/icons-material/CandlestickChartOutlined";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

// import FXMacroContextPanel from "../../components/modals/FXMacroContextPanel";
// import AppsIcon from "@mui/icons-material/Apps";

import useWindowDimensions from "../../utils/width";

const ResultsTracker = () => {
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);
  const mode = localStorage.getItem("equity-mode");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const capitalAccounts = user.capitalAccounts || [];
  const activeAccounts = capitalAccounts.filter((acc) => acc.isActive);

  // Get selected account ID, default based on equity mode if not set
  const getInitialAccountId = () => {
    let accountId = localStorage.getItem("selected-account-id");

    if (!accountId) {
      if (mode === "Personal Capital") {
        const personalAccount = activeAccounts.find(
          (acc) => acc.type === "Personal",
        );
        accountId = personalAccount ? personalAccount.id : "all";
      } else if (mode === "Investor Capital") {
        const investorAccount = activeAccounts.find(
          (acc) => acc.type === "Investor",
        );
        accountId = investorAccount ? investorAccount.id : "all";
      } else {
        accountId = "all";
      }
      localStorage.setItem("selected-account-id", accountId);
      return accountId;
    }

    // Validate that the selected account ID exists in available accounts
    const validAccountIds = [
      "All",
      "All Personal",
      "All Investor",
      ...activeAccounts.map((acc) => acc.id),
    ];

    if (!validAccountIds.includes(accountId)) {
      accountId = "All";
      localStorage.setItem("selected-account-id", accountId);
    }

    return accountId;
  };

  const [selectedAccountId, setSelectedAccountId] = useState(
    getInitialAccountId(),
  );
  const [expandedNotes, setExpandedNotes] = useState({});
  const [quickFilterValue, setQuickFilterValue] = useState("");
  const [multiFilters, setMultiFilters] = useState([]);
  const [logicOperator, setLogicOperator] = useState("or");
  const [starFilters, setStarFilters] = useState({
    executionDiscipline: 0,
    emotionalControl: 0,
    setupQuality: 0,
    executionOperator: ">=",
    emotionalOperator: ">=",
    setupOperator: ">=",
  });

  const { width } = useWindowDimensions();

  // Listen for account-changed events to update selectedAccountId
  useEffect(() => {
    const handleAccountChanged = () => {
      const newAccountId = localStorage.getItem("selected-account-id");
      if (newAccountId && newAccountId !== selectedAccountId) {
        setSelectedAccountId(newAccountId);
      }
    };

    window.addEventListener("account-changed", handleAccountChanged);

    return () => {
      window.removeEventListener("account-changed", handleAccountChanged);
    };
  }, [selectedAccountId]);

  const toggleNotesExpansion = (testId) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [testId]: !prev[testId],
    }));
  };

  // Helper function to apply star filters
  const applyStarFilters = (trade) => {
    const {
      executionDiscipline,
      emotionalControl,
      setupQuality,
      executionOperator,
      emotionalOperator,
      setupOperator,
    } = starFilters;

    // Check execution discipline
    if (executionDiscipline > 0) {
      const value = trade.executionDiscipline || 0;
      if (executionOperator === ">=" && value < executionDiscipline)
        return false;
      if (executionOperator === "<=" && value > executionDiscipline)
        return false;
      if (executionOperator === "=" && value !== executionDiscipline)
        return false;
    }

    // Check emotional control
    if (emotionalControl > 0) {
      const value = trade.emotionalControl || 0;
      if (emotionalOperator === ">=" && value < emotionalControl) return false;
      if (emotionalOperator === "<=" && value > emotionalControl) return false;
      if (emotionalOperator === "=" && value !== emotionalControl) return false;
    }

    // Check setup quality
    if (setupQuality > 0) {
      const value = trade.setupQuality || 0;
      if (setupOperator === ">=" && value < setupQuality) return false;
      if (setupOperator === "<=" && value > setupQuality) return false;
      if (setupOperator === "=" && value !== setupQuality) return false;
    }

    return true;
  };

  // Helper functions to calculate filtered counts
  const getFilteredCount = () => {
    if (
      !quickFilterValue &&
      !starFilters.executionDiscipline &&
      !starFilters.emotionalControl &&
      !starFilters.setupQuality
    )
      return null;
    if (!auth.allTrades) return null;

    const searchValue = quickFilterValue.toLowerCase();
    let trades;
    
    // Check if we're viewing linked trades from a forecast or a single trade
    const linkedTradeIds = localStorage.getItem("linked-trade-ids");
    const tradeToView = localStorage.getItem("trade-to-view");
    
    if (linkedTradeIds) {
      const tradeIds = JSON.parse(linkedTradeIds);
      trades = auth.allTrades.filter((trade) => tradeIds.includes(trade._id));
    } else if (tradeToView) {
      trades = auth.allTrades.filter((trade) => trade._id === tradeToView);
    } else {
      // Get the base trades according to current mode
      if (!auth.backtestingMode && auth.inProgressMode) {
      trades = getFilteredTradesByAccount(
        auth.allTrades
          .filter((trades) => trades.id?.includes("live-"))
          .filter(
            (trades) =>
              trades.outcomePC?.includes("In Progress") ||
              trades.outcomeIC?.includes("In Progress"),
          ),
        selectedAccountId,
      );
    } else if (!auth.backtestingMode && !auth.inProgressMode) {
      trades = getFilteredTradesByAccount(
        auth.allTrades.filter((trades) => trades.id?.includes("live-")),
        selectedAccountId,
      );
    } else if (auth.backtestingMode) {
      trades = auth.allTrades.filter(
        (trades) =>
          trades.testId?.includes(showSpecficTest) &&
          trades.id?.includes("backtest-"),
      );
    } else {
      trades = auth.allTrades.filter((trades) =>
        trades.id?.includes("backtest-"),
      );
    }
    }

    // Apply quick filter and star filters
    const filtered = trades.filter((trade) => {
      // Apply star filters first
      if (!applyStarFilters(trade)) return false;

      // If no text search, return true
      if (!quickFilterValue) return true;

      // Search in pair/instrument
      if (trade.pair && trade.pair.toLowerCase().includes(searchValue)) {
        return true;
      }

      // Search in direction
      if (
        trade.direction &&
        trade.direction.toLowerCase().includes(searchValue)
      ) {
        return true;
      }

      // Search in outcome (both PC and IC)
      if (
        trade.outcomePC &&
        trade.outcomePC.toLowerCase().includes(searchValue)
      ) {
        return true;
      }
      if (
        trade.outcomeIC &&
        trade.outcomeIC.toLowerCase().includes(searchValue)
      ) {
        return true;
      }

      // Search in grade
      if (trade.grade && trade.grade.toLowerCase().includes(searchValue)) {
        return true;
      }

      // Search in beta pattern
      if (trade.betaPattern && trade.betaPattern.toLowerCase().includes(searchValue)) {
        return true;
      }

      // Search in alpha pattern
      if (trade.alphaPattern && trade.alphaPattern.toLowerCase().includes(searchValue)) {
        return true;
      }

      // Search in watchlist (highlightedPair)
      if (
        trade.highlightedPair &&
        trade.highlightedPair.toLowerCase().includes(searchValue)
      ) {
        return true;
      }

      // Search in entry number (firstEntryOrScaleIn)
      if (
        trade.firstEntryOrScaleIn &&
        trade.firstEntryOrScaleIn.toLowerCase().includes(searchValue)
      ) {
        return true;
      }

      // Search in entry type
      if (
        trade.entryType &&
        trade.entryType.toLowerCase().includes(searchValue)
      ) {
        return true;
      }

      return false;
    });

    return filtered.length;
  };

  const getTotalCount = () => {
    if (!auth.allTrades) return null;

    // Check if we're viewing linked trades from a forecast or a single trade
    const linkedTradeIds = localStorage.getItem("linked-trade-ids");
    const tradeToView = localStorage.getItem("trade-to-view");
    
    if (linkedTradeIds) {
      const tradeIds = JSON.parse(linkedTradeIds);
      return auth.allTrades.filter((trade) => tradeIds.includes(trade._id)).length;
    } else if (tradeToView) {
      return auth.allTrades.filter((trade) => trade._id === tradeToView).length;
    }

    // Get the base trades count according to current mode
    if (!auth.backtestingMode && auth.inProgressMode) {
      return getFilteredTradesByAccount(
        auth.allTrades
          .filter((trades) => trades.id?.includes("live-"))
          .filter(
            (trades) =>
              trades.outcomePC?.includes("In Progress") ||
              trades.outcomeIC?.includes("In Progress"),
          ),
        selectedAccountId,
      ).length;
    } else if (!auth.backtestingMode && !auth.inProgressMode) {
      return getFilteredTradesByAccount(
        auth.allTrades.filter((trades) => trades.id?.includes("live-")),
        selectedAccountId,
      ).length;
    } else if (auth.backtestingMode) {
      return auth.allTrades.filter(
        (trades) =>
          trades.testId?.includes(showSpecficTest) &&
          trades.id?.includes("backtest-"),
      ).length;
    } else {
      return auth.allTrades.filter((trades) => trades.id?.includes("backtest-"))
        .length;
    }
  };

  // Helper function to filter trades by account
  const getFilteredTradesByAccount = (trades, accountId) => {
    if (!accountId || accountId === "All") {
      return trades;
    }

    if (accountId === "All Personal") {
      const personalTrades = trades.filter(
        (trade) => trade.equityType === "Personal Capital",
      );
      return personalTrades;
    }

    if (accountId === "All Investor") {
      const investorTrades = trades.filter(
        (trade) => trade.equityType === "Investor Capital",
      );
      return investorTrades;
    }

    // Filter by specific account ID
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const capitalAccounts = user.capitalAccounts || [];
    const selectedAccount = capitalAccounts.find((acc) => acc.id === accountId);

    if (!selectedAccount) {
      return trades;
    }

    return trades.filter((trade) => {
      // If trade has accountId field, use it
      if (trade.accountId) {
        return trade.accountId === accountId;
      }
      // Otherwise, fall back to equityType matching
      if (selectedAccount.type === "Personal") {
        return trade.equityType === "Personal Capital";
      } else if (selectedAccount.type === "Investor") {
        return trade.equityType === "Investor Capital";
      }
      return false;
    });
  };

  // Get filtered trades for the data grid based on selected account
  const filteredTradesForGrid = useMemo(() => {
    // Check if we're viewing linked trades from a forecast or a single trade
    const linkedTradeIds = localStorage.getItem("linked-trade-ids");
    const tradeToView = localStorage.getItem("trade-to-view");
    
    if (linkedTradeIds) {
      const tradeIds = JSON.parse(linkedTradeIds);
      const linkedTrades = auth.allTrades ? auth.allTrades.filter((trade) => tradeIds.includes(trade._id)) : [];
      return linkedTrades.filter(applyStarFilters);
    } else if (tradeToView) {
      const singleTrade = auth.allTrades ? auth.allTrades.filter((trade) => trade._id === tradeToView) : [];
      return singleTrade.filter(applyStarFilters);
    }
    
    if (!auth.allTrades || auth.allTrades.length === 0) {
      if (auth.trades && auth.trades.length > 0) {
        // For the rest of this function, use auth.trades instead of auth.allTrades
        const fallbackTrades = auth.trades;

        let baseTrades;
        if (!auth.backtestingMode && auth.inProgressMode) {
          baseTrades = fallbackTrades
            .filter((trades) => trades.id?.includes("live-"))
            .filter(
              (trades) =>
                trades.outcomePC?.includes("In Progress") ||
                trades.outcomeIC?.includes("In Progress"),
            );
        } else if (!auth.backtestingMode && !auth.inProgressMode) {
          baseTrades = fallbackTrades.filter((trades) =>
            trades.id?.includes("live-"),
          );
        } else {
          return null;
        }

        // Apply year filter if not in backtesting mode
        if (!auth.backtestingMode && localStorage.getItem("year") !== "0") {
          const currentYearSet = moment(localStorage.getItem("year")).format("YY");
          baseTrades = baseTrades.filter((t) => {
            if (!t.entryDate) return false;
            const dateArray = t.entryDate.split("/");
            const tradeYear = dateArray[2];
            return Number(tradeYear) === Number(currentYearSet);
          });
        }

        const filteredTrades = getFilteredTradesByAccount(
          baseTrades,
          selectedAccountId,
        );

        // Apply star filters
        return filteredTrades.filter(applyStarFilters);
      }
      return [];
    }

    let baseTrades;
    if (!auth.backtestingMode && auth.inProgressMode) {
      baseTrades = auth.allTrades
        .filter((trades) => trades.id?.includes("live-"))
        .filter(
          (trades) =>
            trades.outcomePC?.includes("In Progress") ||
            trades.outcomeIC?.includes("In Progress"),
        );
    } else if (!auth.backtestingMode && !auth.inProgressMode) {
      baseTrades = auth.allTrades.filter((trades) =>
        trades.id?.includes("live-"),
      );
    } else {
      return null;
    }

    // Apply year filter if not in backtesting mode
    if (!auth.backtestingMode && localStorage.getItem("year") !== "0") {
      const currentYearSet = moment(localStorage.getItem("year")).format("YY");
      baseTrades = baseTrades.filter((t) => {
        if (!t.entryDate) return false;
        const dateArray = t.entryDate.split("/");
        const tradeYear = dateArray[2];
        return Number(tradeYear) === Number(currentYearSet);
      });
    }

    const filteredTrades = getFilteredTradesByAccount(
      baseTrades,
      selectedAccountId,
    );

    // Apply star filters
    return filteredTrades.filter(applyStarFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAccountId,
    auth.allTrades,
    auth.trades,
    auth.backtestingMode,
    auth.inProgressMode,
    starFilters,
    applyStarFilters,
  ]);

  const needsExpansion = (notes) => {
    if (!notes) return false;
    // Rough estimation: if notes have more than 100 characters or contain multiple line breaks
    return notes.length > 100 || (notes.match(/\n/g) || []).length > 1;
  };

  const getViewModeColor = () => {
    switch (auth.viewMode) {
      case "all-view":
        return "#3b82f6";
      case "simple-view":
        return "#9c27b0";
      case "edit-view":
        return "#f59e0b";
      case "performance-view":
        return "#3b82f6";
      case "risk-view":
        return "#3b82f6";
      case "journal-view":
        return "#3b82f6";
      default:
        return "#3b82f6";
    }
  };

  const getViewModeLabel = () => {
    switch (auth.viewMode) {
      case "all-view":
        return "All View";
      case "simple-view":
        return "Simple View";
      case "edit-view":
        return "Edit View";
      case "performance-view":
        return "Performance View";
      case "risk-view":
        return "Risk View";
      case "journal-view":
        return "Journal View";
      default:
        return "View Mode";
    }
  };

  const getViewModeIcon = () => {
    switch (auth.viewMode) {
      case "all-view":
        return "All";
      case "simple-view":
        return "Simple";
      case "edit-view":
        return "Edit";

      default:
        return "";
    }
  };

  const {
    calculateFields,
    allbackTestSchemas,
    setAllbackTestSchemas,
    showBacktestTrades,
    remove,
    handleRemove,
    handleOpen,
    realisedPercentage,
    realisedReturn,
    realisedReturnGross,
    realisedPercentageWithFees,
    selectedArchivedTests,
    setSelectedArchivedTests,
    handleOpenBacktest,
    handleOpenEditBacktest,
    editBacktestMode,
    editBacktestData,
    handleArchivingTest,
    handleDeleteTest,
    setSelectedArchivedTestsBoolean,
    selectedArchivedTestsBoolean,
    handleUnArchivedTest,
    showSpecficTest,
    setShowSpecficTest,
    realisedPercentageBacktest,
    realisedReturnBacktest,
    realisedReturnGrossBacktest,
    realisedPercentageWithFeesBacktest,
    handleInProgressMode,
    handleEditCell,
    setToRemove,
    toRemove,
    idTradeGenerator,
    runDurationCal,
    handleOpenJournal,
    handleRemoveButton,
    handleClose,
    open,
    handleCloseBacktest,
    openNewTest,
    handleCloseJournal,
    openJournal,
    handleSimpleView,
    formSubmitted,
    // handleCloseCal,
    // openCal,
    // handleOpenCal,
    calculateOutcome,
    equityCurve,
    setEquityCurve,
    // openMacro,
    // handleOpenMacro,
    // handleCloseMacro,
    openForecast,
    handleOpenForecast,
    handleCloseForecast,
    forecastPrefilledData,
  } = Logic();

  // Handler to refresh data without page reload
  const handleRefresh = () => {
    if (auth.trades && auth.trades.length > 0 && calculateFields) {
      const filteredTrades = getFilteredTradesByAccount(
        auth.trades,
        selectedAccountId,
      );

      // Get current rotater value - this is what the year button cycle uses
      const currentRotater = localStorage.getItem("rotater") || "Start Of Year";

      calculateFields(
        filteredTrades,
        auth.backtestingMode,
        auth.backtestingMode ? localStorage.getItem("backtest-active") : "",
        currentRotater,
        undefined,
        selectedAccountId,
      );
    }
  };

  // Recalculate on mount with filtered trades
  useEffect(() => {
    if (auth.trades && auth.trades.length > 0 && calculateFields) {
      const filteredTrades = getFilteredTradesByAccount(
        auth.trades,
        selectedAccountId,
      );
      // Always pass rotater to ensure stats calculate correctly
      const rotaterParam = localStorage.getItem("rotater") || "Start Of Year";

      calculateFields(
        filteredTrades,
        auth.backtestingMode,
        auth.backtestingMode ? localStorage.getItem("backtest-active") : "",
        rotaterParam,
        undefined, // equityCurve
        selectedAccountId,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, auth.trades?.length]); // Run when account changes or trades load

  // Handler to clear backtest dropdown selection
  const handleClearBacktest = () => {
    setShowSpecficTest("");
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#f9fafb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          gap: "12px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: width <= 500 ? "center" : "space-between",
          alignItems: "center",
          width: "100%",
          margin: "0",
          padding: "16px 20px",
          color: "#FCFCFF",
          backgroundColor: theme[0] === "dark" ? "#25262b" : "#ffffff",
          borderBottom:
            theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "flex-start",
            width: "auto",
          }}
        >
          {!equityCurve && width > 500 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Tooltip title={getViewModeLabel()} arrow>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSimpleView}
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    minHeight: "32px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? `${getViewModeColor()}20`
                        : `${getViewModeColor()}15`,
                    color: getViewModeColor(),
                    border:
                      theme[0] === "dark"
                        ? `1px solid ${getViewModeColor()}40`
                        : `1px solid ${getViewModeColor()}30`,
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme[0] === "dark"
                          ? `${getViewModeColor()}30`
                          : `${getViewModeColor()}20`,
                      borderColor:
                        theme[0] === "dark"
                          ? `${getViewModeColor()}50`
                          : `${getViewModeColor()}40`,
                    },
                  }}
                >
                  {getViewModeIcon()}
                </Button>
              </Tooltip>

              <Tooltip
                title={auth.inProgressMode ? "Live Trades" : "Closed Trades"}
                arrow
              >
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    handleInProgressMode({
                      target: { checked: !auth.inProgressMode },
                    })
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    minHeight: "32px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.05)"
                      : theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.15)"
                        : "rgba(38, 166, 93, 0.1)",
                    color: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "#ffffff"
                        : "#000000"
                      : "#26a65d",
                    border: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.3)"
                        : "1px solid rgba(0, 0, 0, 0.2)"
                      : theme[0] === "dark"
                        ? "1px solid rgba(38, 166, 93, 0.4)"
                        : "1px solid rgba(38, 166, 93, 0.3)",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: auth.inProgressMode
                        ? theme[0] === "dark"
                          ? "rgba(255, 255, 255, 0.25)"
                          : "rgba(0, 0, 0, 0.1)"
                        : theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.25)"
                          : "rgba(38, 166, 93, 0.15)",
                      borderColor: auth.inProgressMode
                        ? theme[0] === "dark"
                          ? "rgba(255, 255, 255, 0.4)"
                          : "rgba(0, 0, 0, 0.3)"
                        : theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.5)"
                          : "rgba(38, 166, 93, 0.4)",
                    },
                  }}
                >
                  {auth.inProgressMode ? (
                    <HourglassEmptyIcon sx={{ fontSize: "20px" }} />
                  ) : (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: "20px", color: "#26a65d" }}
                    />
                  )}
                </Button>
              </Tooltip>
            </div>
          )}
        </div>

        {!equityCurve && !auth.backtestingMode && (
          <div
            style={{
              display: "flex",
              justifyContent: width <= 500 ? "center" : "center",
              width: width <= 500 ? "100%" : "auto",
              order: width <= 500 ? -1 : 0,
              marginLeft: "1em",
            }}
          >
            <StatsBar
              realisedPercentage={realisedPercentage}
              realisedPercentageWithFees={realisedPercentageWithFees}
              realisedPercentageBacktest={realisedPercentageBacktest}
              realisedPercentageWithFeesBacktest={
                realisedPercentageWithFeesBacktest
              }
              realisedReturn={realisedReturn}
              realisedReturnGross={realisedReturnGross}
              realisedReturnBacktest={realisedReturnBacktest}
              realisedReturnGrossBacktest={realisedReturnGrossBacktest}
              calculateFields={calculateFields}
              showBacktestTrades={showBacktestTrades}
              selectedAccountId={selectedAccountId}
              getFilteredTradesByAccount={getFilteredTradesByAccount}
            />
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: width <= 500 ? "center" : "flex-end",
            alignItems: "center",
            gap: "16px",
            width: "auto",
            height: "auto",
          }}
        >
          {width <= 390 && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Tooltip title={getViewModeLabel()} arrow>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSimpleView}
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "6px 8px",
                    minHeight: "32px",
                    minWidth: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? `${getViewModeColor()}20`
                        : `${getViewModeColor()}15`,
                    color: getViewModeColor(),
                    border:
                      theme[0] === "dark"
                        ? `1px solid ${getViewModeColor()}40`
                        : `1px solid ${getViewModeColor()}30`,
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme[0] === "dark"
                          ? `${getViewModeColor()}30`
                          : `${getViewModeColor()}20`,
                      borderColor:
                        theme[0] === "dark"
                          ? `${getViewModeColor()}50`
                          : `${getViewModeColor()}40`,
                    },
                  }}
                >
                  {getViewModeIcon()}
                </Button>
              </Tooltip>

              <Tooltip
                title={auth.inProgressMode ? "Live Trades" : "Closed Trades"}
                arrow
              >
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    handleInProgressMode({
                      target: { checked: !auth.inProgressMode },
                    })
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "6px 8px",
                    minHeight: "32px",
                    minWidth: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    backgroundColor: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.05)"
                      : theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.15)"
                        : "rgba(38, 166, 93, 0.1)",
                    color: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "#ffffff"
                        : "#000000"
                      : "#26a65d",
                    border: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.3)"
                        : "1px solid rgba(0, 0, 0, 0.2)"
                      : theme[0] === "dark"
                        ? "1px solid rgba(38, 166, 93, 0.4)"
                        : "1px solid rgba(38, 166, 93, 0.3)",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: auth.inProgressMode
                        ? theme[0] === "dark"
                          ? "rgba(255, 255, 255, 0.25)"
                          : "rgba(0, 0, 0, 0.1)"
                        : theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.25)"
                          : "rgba(38, 166, 93, 0.15)",
                      borderColor: auth.inProgressMode
                        ? theme[0] === "dark"
                          ? "rgba(255, 255, 255, 0.4)"
                          : "rgba(0, 0, 0, 0.3)"
                        : theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.5)"
                          : "rgba(38, 166, 93, 0.4)",
                    },
                  }}
                >
                  {auth.inProgressMode ? (
                    <HourglassEmptyIcon sx={{ fontSize: "20px" }} />
                  ) : (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: "20px", color: "#26a65d" }}
                    />
                  )}
                </Button>
              </Tooltip>
            </div>
          )}
          {width > 390 && width <= 500 && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleSimpleView}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  borderRadius: "8px",
                  padding: "5px 12px",
                  minHeight: "32px",
                  minWidth: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  backgroundColor:
                    theme[0] === "dark"
                      ? `${getViewModeColor()}20`
                      : `${getViewModeColor()}15`,
                  color: getViewModeColor(),
                  border:
                    theme[0] === "dark"
                      ? `1px solid ${getViewModeColor()}40`
                      : `1px solid ${getViewModeColor()}30`,
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor:
                      theme[0] === "dark"
                        ? `${getViewModeColor()}30`
                        : `${getViewModeColor()}20`,
                    borderColor:
                      theme[0] === "dark"
                        ? `${getViewModeColor()}50`
                        : `${getViewModeColor()}40`,
                  },
                }}
              >
                {getViewModeIcon()}
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={() =>
                  handleInProgressMode({
                    target: { checked: !auth.inProgressMode },
                  })
                }
                sx={{
                  textTransform: "none",
                  fontSize: "10px",
                  borderRadius: "8px",
                  padding: "6px 13px",
                  minHeight: "32px",
                  minWidth: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  backgroundColor: auth.inProgressMode
                    ? theme[0] === "dark"
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(0, 0, 0, 0.05)"
                    : theme[0] === "dark"
                      ? "rgba(38, 166, 93, 0.15)"
                      : "rgba(38, 166, 93, 0.1)",
                  color: auth.inProgressMode
                    ? theme[0] === "dark"
                      ? "#ffffff"
                      : "#000000"
                    : "#26a65d",
                  border: auth.inProgressMode
                    ? theme[0] === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.3)"
                      : "1px solid rgba(0, 0, 0, 0.2)"
                    : theme[0] === "dark"
                      ? "1px solid rgba(38, 166, 93, 0.4)"
                      : "1px solid rgba(38, 166, 93, 0.3)",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "rgba(255, 255, 255, 0.25)"
                        : "rgba(0, 0, 0, 0.1)"
                      : theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.25)"
                        : "rgba(38, 166, 93, 0.15)",
                    borderColor: auth.inProgressMode
                      ? theme[0] === "dark"
                        ? "rgba(255, 255, 255, 0.4)"
                        : "rgba(0, 0, 0, 0.3)"
                      : theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.5)"
                        : "rgba(38, 166, 93, 0.4)",
                  },
                }}
              >
                {auth.inProgressMode ? (
                  <HourglassEmptyIcon sx={{ fontSize: "20px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "20px", color: "#26a65d" }}
                  />
                )}
              </Button>
            </div>
          )}
          {remove ? (
            <div
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <DeleteOutlinedIcon
                fontSize="large"
                sx={{
                  color: "#f23645",
                  marginRight: "5px",
                }}
              />
              <p style={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}>
                Delete
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flex: "0 0 auto",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* <Tooltip
                title="FXMacroContextPanel"
                fontSize="large"
                sx={{
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  cursor: "pointer",
                }}
              >
                <CandlestickChartOutlinedIcon
                  sx={{
                    color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    cursor: "pointer",
                    // marginRight: "15px",
                  }}
                  fontSize="large"
                  onClick={handleOpenMacro}
                />
              </Tooltip> */}

              {!equityCurve && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  {auth.backtestingMode && !remove && !equityCurve ? (
                    <Tooltip title="New Backtest">
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleOpenBacktest}
                        sx={{
                          textTransform: "none",
                          fontSize: "13px",
                          borderRadius: "8px",
                          padding: "6px 14px",
                          minHeight: "32px",
                          minWidth: width <= 500 ? "32px" : "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(249, 115, 22, 0.15)"
                              : "rgba(249, 115, 22, 0.1)",
                          color: "#f97316",
                          border:
                            theme[0] === "dark"
                              ? "1px solid rgba(249, 115, 22, 0.4)"
                              : "1px solid rgba(249, 115, 22, 0.3)",
                          fontWeight: "600",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor:
                              theme[0] === "dark"
                                ? "rgba(249, 115, 22, 0.25)"
                                : "rgba(249, 115, 22, 0.15)",
                            borderColor:
                              theme[0] === "dark"
                                ? "rgba(249, 115, 22, 0.5)"
                                : "rgba(249, 115, 22, 0.4)",
                          },
                        }}
                      >
                        <BarChartIcon
                          sx={{ fontSize: width <= 500 ? "20px" : "18px" }}
                        />
                        {width > 500 && "Backtest"}
                      </Button>
                    </Tooltip>
                  ) : null}

                  <Tooltip title="New Trade">
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleOpen}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        borderRadius: "8px",
                        padding: "6px 14px",
                        minHeight: "32px",
                        minWidth: width <= 500 ? "32px" : "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)",
                        color: "#26a65d",
                        border:
                          theme[0] === "dark"
                            ? "1px solid rgba(38, 166, 93, 0.4)"
                            : "1px solid rgba(38, 166, 93, 0.3)",
                        fontWeight: "600",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.25)"
                              : "rgba(38, 166, 93, 0.15)",
                          borderColor:
                            theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.5)"
                              : "rgba(38, 166, 93, 0.4)",
                        },
                      }}
                    >
                      <CandlestickChartOutlinedIcon
                        sx={{ fontSize: width <= 500 ? "20px" : "18px" }}
                      />
                      {width > 500 && "Entry"}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflow: "hidden",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {auth.backtestingMode ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              margin: "1.2em 1em",
            }}
          >
            {/* Dropdowns Row */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: width <= 768 ? "center" : "space-around",
                flexDirection: width <= 768 ? "column" : "row",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <div
                style={{
                  width: width <= 768 ? "100%" : "auto",
                  maxWidth: width <= 768 ? "400px" : "none",
                  margin: "0 auto",
                }}
              >
                <UnarchivedTestsDropDown
                  selectedTest={showSpecficTest}
                  onTestSelect={(testId) => {
                    // The selection is handled internally by the dropdown
                  }}
                  showBacktestTrades={showBacktestTrades}
                  label="Select Test"
                  includeMissedTrades={true}
                  variant={width <= 500 ? "compact" : "default"}
                  fullWidth={width <= 768}
                />
              </div>

              <div
                style={{
                  width: width <= 768 ? "100%" : "auto",
                  maxWidth: width <= 768 ? "400px" : "none",
                  margin: "0 auto",
                }}
              >
                <ArchivedTestsDropDown
                  selectedArchivedTestsBoolean={selectedArchivedTestsBoolean}
                  selectedArchivedTests={selectedArchivedTests}
                  setSelectedArchivedTestsBoolean={
                    setSelectedArchivedTestsBoolean
                  }
                  setSelectedArchivedTests={setSelectedArchivedTests}
                  allbackTestSchemas={allbackTestSchemas}
                  variant={width <= 500 ? "compact" : "default"}
                  fullWidth={width <= 768}
                />
              </div>
            </div>

            {/* Blue Accordion (Centered Below) */}
            {width > 1024 &&
              allbackTestSchemas
                ?.filter((tests) => tests.archived?.includes("false"))
                .map((t) => {
                  return showSpecficTest === t.testId ? (
                    <div
                      key={t.testId}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          width: "100%",
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(59, 130, 246, 0.08)"
                              : "rgba(59, 130, 246, 0.04)",
                          border: `1px solid ${theme[0] === "dark" ? "#3b82f6" : "#93c5fd"}`,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          paddingLeft: "16px",
                          paddingRight: "16px",
                        }}
                      >
                        {/* Title Section - Always Visible */}
                        <div
                          style={{
                            padding: "16px",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                          onClick={() => toggleNotesExpansion(t.testId)}
                        >
                          <div
                            style={{
                              color: "#3b82f6",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            {t.testId}
                          </div>
                          <div
                            style={{
                              color: "#3b82f6",
                              fontSize: "12px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {expandedNotes[t.testId] ? "See less" : "See more"}
                            <span
                              style={{
                                display: "inline-block",
                                transition: "transform 0.3s ease",
                                transform: expandedNotes[t.testId]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                              }}
                            >
                              ▼
                            </span>
                          </div>
                        </div>

                        {/* Expandable Content */}
                        <div
                          style={{
                            maxHeight: expandedNotes[t.testId] ? "500px" : "0",
                            overflow: "hidden",
                            transition: "max-height 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              padding: "0 16px 16px",
                              borderTop: `1px solid ${theme[0] === "dark" ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"}`,
                            }}
                          >
                            <p
                              style={{
                                color:
                                  theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
                                fontSize: "13px",
                                fontWeight: "500",
                                margin: "12px 0",
                                lineHeight: "1.4",
                              }}
                            >
                              {t.pair}{" "}
                              {(() => {
                                const weeks = moment(t.exitDate).diff(
                                  moment(t.startDate),
                                  "weeks",
                                );
                                const days = moment(t.exitDate).diff(
                                  moment(t.startDate),
                                  "days",
                                );

                                if (weeks === 0) {
                                  return `${days} ${days === 1 ? "Day" : "Days"} Of Data`;
                                }
                                return `${weeks} ${weeks === 1 ? "Week" : "Weeks"} Of Data`;
                              })()}
                              <br />
                              {moment(t.startDate).format(
                                "Do MMM YYYY",
                              )} to {moment(t.exitDate).format("Do MMM YYYY")}
                            </p>
                            <div>
                              {needsExpansion(t.notes) ? (
                                <div>
                                  <p
                                    style={{
                                      color:
                                        theme[0] === "dark"
                                          ? "#9ca3af"
                                          : "#6b7280",
                                      fontSize: "12px",
                                      whiteSpace: "pre-wrap",
                                      margin: "6px 0 0 0",
                                      fontStyle: "italic",
                                      overflow: "hidden",
                                      display: "-webkit-box",
                                      WebkitLineClamp: expandedNotes[t.testId]
                                        ? "unset"
                                        : "1",
                                      WebkitBoxOrient: "vertical",
                                    }}
                                  >
                                    {t.notes}
                                  </p>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleNotesExpansion(t.testId);
                                    }}
                                    style={{
                                      color: "#3b82f6",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      marginTop: "4px",
                                      textAlign: "center",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {expandedNotes[t.testId]
                                      ? "Show less"
                                      : "Read more"}
                                  </div>
                                </div>
                              ) : (
                                <p
                                  style={{
                                    color:
                                      theme[0] === "dark"
                                        ? "#9ca3af"
                                        : "#6b7280",
                                    fontSize: "12px",
                                    whiteSpace: "pre-wrap",
                                    margin: "6px 0 0 0",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {t.notes}
                                </p>
                              )}
                            </div>

                            {/* Modern Action Buttons at Bottom */}
                            {t.testId !== "Missed Trades" && !formSubmitted && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  marginTop: "16px",
                                  justifyContent: "center",
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArchivingTest(t);
                                  }}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                    border: "1px solid #26a65d",
                                    backgroundColor: "transparent",
                                    color: "#26a65d",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                  onMouseOver={(e) => {
                                    e.target.style.backgroundColor =
                                      "rgba(38, 166, 93, 0.1)";
                                    e.target.style.transform =
                                      "translateY(-1px)";
                                  }}
                                  onMouseOut={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                    e.target.style.transform = "translateY(0)";
                                  }}
                                >
                                  <UnarchiveIcon
                                    sx={{
                                      fontSize: "16px",
                                    }}
                                  />
                                  Archive
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTest(
                                      JSON.stringify([t._id, t.testId]),
                                    );
                                  }}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                    border: "1px solid #f23645",
                                    backgroundColor: "transparent",
                                    color: "#f23645",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                  onMouseOver={(e) => {
                                    e.target.style.backgroundColor =
                                      "rgba(242, 54, 69, 0.1)";
                                    e.target.style.transform =
                                      "translateY(-1px)";
                                  }}
                                  onMouseOut={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                    e.target.style.transform = "translateY(0)";
                                  }}
                                >
                                  <DeleteOutlinedIcon
                                    sx={{
                                      fontSize: "16px",
                                    }}
                                  />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })}

            {/* Unarchive / Delete Buttons */}
            {selectedArchivedTestsBoolean && (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {!formSubmitted && (
                  <button
                    onClick={() => handleUnArchivedTest(selectedArchivedTests)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "1px solid #26a65d",
                      backgroundColor: "transparent",
                      color: "#26a65d",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "rgba(38, 166, 93, 0.1)";
                      e.target.style.transform = "translateY(-1px)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    <UnarchiveIcon
                      sx={{
                        fontSize: "16px",
                      }}
                    />
                    Unarchive
                  </button>
                )}
                {!formSubmitted && (
                  <button
                    onClick={() => {
                      const selectedBacktest = auth.backtests.find(
                        (backtest) =>
                          backtest._id === JSON.parse(selectedArchivedTests)[0],
                      );
                      if (selectedBacktest) {
                        handleOpenEditBacktest(selectedBacktest);
                      }
                    }}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "1px solid #3b82f6",
                      backgroundColor: "transparent",
                      color: "#3b82f6",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(59, 130, 246, 0.1)";
                      e.target.style.transform = "translateY(-1px)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    <EditIcon
                      sx={{
                        fontSize: "16px",
                      }}
                    />
                    Edit
                  </button>
                )}
                {!formSubmitted && (
                  <button
                    onClick={() => handleDeleteTest(selectedArchivedTests)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "1px solid #f23645",
                      backgroundColor: "transparent",
                      color: "#f23645",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "rgba(242, 54, 69, 0.1)";
                      e.target.style.transform = "translateY(-1px)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    <DeleteOutlinedIcon
                      sx={{
                        fontSize: "16px",
                      }}
                    />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* ------------------ Test Information Text (Mobile Only) starts here ------------------ */}
        {width <= 1024 &&
          allbackTestSchemas
            ?.filter((tests) => tests.archived?.includes("false"))
            .map((t) => {
              return showSpecficTest === t.testId ? (
                <div
                  key={t.testId}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    marginLeft: "12px",
                    marginRight: "12px",
                    marginTop: "12px",
                    marginBottom: "32px",
                  }}
                >
                  {/* Accordion Blue Box */}
                  <div
                    style={{
                      textAlign: "center",
                      width: "100%",
                      borderRadius: "8px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(59, 130, 246, 0.08)"
                          : "rgba(59, 130, 246, 0.04)",
                      border: `1px solid ${theme[0] === "dark" ? "#3b82f6" : "#93c5fd"}`,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      maxWidth: "400px",
                    }}
                  >
                    {/* Title Section - Always Visible */}
                    <div
                      style={{
                        padding: "12px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onClick={() => toggleNotesExpansion(t.testId)}
                    >
                      <div
                        style={{
                          color: "#3b82f6",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {t.testId}
                      </div>
                      <div
                        style={{
                          color: "#3b82f6",
                          fontSize: "12px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {expandedNotes[t.testId] ? "See less" : "See more"}
                        <span
                          style={{
                            display: "inline-block",
                            transition: "transform 0.3s ease",
                            transform: expandedNotes[t.testId]
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        >
                          ▼
                        </span>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    <div
                      style={{
                        maxHeight: expandedNotes[t.testId] ? "500px" : "0",
                        overflow: "hidden",
                        transition: "max-height 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          padding: "0 16px 16px",
                          borderTop: `1px solid ${theme[0] === "dark" ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"}`,
                        }}
                      >
                        <p
                          style={{
                            color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
                            fontSize: "13px",
                            fontWeight: "500",
                            margin: "12px 0",
                            lineHeight: "1.4",
                          }}
                        >
                          {t.pair}{" "}
                          {(() => {
                            const weeks = moment(t.exitDate).diff(
                              moment(t.startDate),
                              "weeks",
                            );
                            const days = moment(t.exitDate).diff(
                              moment(t.startDate),
                              "days",
                            );

                            if (weeks === 0) {
                              return `${days} ${days === 1 ? "Day" : "Days"} Of Data`;
                            }
                            return `${weeks} ${weeks === 1 ? "Week" : "Weeks"} Of Data`;
                          })()}
                          <br />
                          {moment(t.startDate).format("Do MMM YYYY")} to{" "}
                          {moment(t.exitDate).format("Do MMM YYYY")}
                        </p>
                        <div>
                          {needsExpansion(t.notes) ? (
                            <div>
                              <p
                                style={{
                                  color:
                                    theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                  fontSize: "12px",
                                  whiteSpace: "pre-wrap",
                                  margin: "6px 0 0 0",
                                  fontStyle: "italic",
                                  overflow: "hidden",
                                  display: "-webkit-box",
                                  WebkitLineClamp: expandedNotes[t.testId]
                                    ? "unset"
                                    : "1",
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {t.notes}
                              </p>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleNotesExpansion(t.testId);
                                }}
                                style={{
                                  color: "#3b82f6",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  marginTop: "4px",
                                  textAlign: "center",
                                  textDecoration: "underline",
                                }}
                              >
                                {expandedNotes[t.testId]
                                  ? "Show less"
                                  : "Read more"}
                              </div>
                            </div>
                          ) : (
                            <p
                              style={{
                                color:
                                  theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                fontSize: "12px",
                                whiteSpace: "pre-wrap",
                                margin: "6px 0 0 0",
                                fontStyle: "italic",
                              }}
                            >
                              {t.notes}
                            </p>
                          )}
                        </div>

                        {/* Modern Action Buttons at Bottom */}
                        {t.testId !== "Missed Trades" && !formSubmitted && (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "16px",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchivingTest(t);
                              }}
                              style={{
                                padding: "8px 16px",
                                borderRadius: "8px",
                                border: "1px solid #26a65d",
                                backgroundColor: "transparent",
                                color: "#26a65d",
                                fontSize: "12px",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor =
                                  "rgba(38, 166, 93, 0.1)";
                                e.target.style.transform = "translateY(-1px)";
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = "transparent";
                                e.target.style.transform = "translateY(0)";
                              }}
                            >
                              <UnarchiveIcon
                                sx={{
                                  fontSize: "16px",
                                }}
                              />
                              Archive
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTest(
                                  JSON.stringify([t._id, t.testId]),
                                );
                              }}
                              style={{
                                padding: "8px 16px",
                                borderRadius: "8px",
                                border: "1px solid #f23645",
                                backgroundColor: "transparent",
                                color: "#f23645",
                                fontSize: "12px",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor =
                                  "rgba(242, 54, 69, 0.1)";
                                e.target.style.transform = "translateY(-1px)";
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = "transparent";
                                e.target.style.transform = "translateY(0)";
                              }}
                            >
                              <DeleteOutlinedIcon
                                sx={{
                                  fontSize: "16px",
                                }}
                              />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })}

        {/* ------------------ DataTable starts here ------------------ */}

        {!equityCurve ? (
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <CustomDataGrid
              key={`${auth.viewMode}-${selectedAccountId}`}
              handleEditCell={handleEditCell}
              showSpecficTest={showSpecficTest}
              setToRemove={setToRemove}
              inProgressMode={auth.inProgressMode}
              viewMode={auth.viewMode}
              selectedAccountId={selectedAccountId}
              quickFilterValues={quickFilterValue ? [quickFilterValue] : []}
              quickFilterValue={quickFilterValue}
              filteredTrades={filteredTradesForGrid}
              setQuickFilterValue={setQuickFilterValue}
              getFilteredCount={getFilteredCount}
              getTotalCount={getTotalCount}
              multiFilters={multiFilters}
              logicOperator={logicOperator}
              onRefresh={handleRefresh}
              onClearBacktest={handleClearBacktest}
              columns={
                auth.viewMode === "all-view"
                  ? columnsAll(
                      theme,
                      throwMessage,
                      idTradeGenerator,
                      runDurationCal,
                      handleOpenJournal,
                      auth,
                      calculateFields,
                    )
                  : auth.viewMode === "simple-view"
                    ? columnsSimple(
                        theme,
                        throwMessage,
                        idTradeGenerator,
                        runDurationCal,
                        handleOpenJournal,
                        auth,
                      )
                    : auth.viewMode === "edit-view"
                      ? columnsEdit(
                          theme,
                          throwMessage,
                          idTradeGenerator,
                          runDurationCal,
                          handleOpenJournal,
                          auth,
                          calculateFields,
                        )
                      : []
              }
              handleRemoveButton={handleRemoveButton}
            />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              height: "100%",
            }}
          >
            <EquityCurve
              calculateFields={calculateFields}
              trades={auth.trades}
              equityCurve={equityCurve}
              setEquityCurve={setEquityCurve}
            />
          </div>
        )}

        {/* ------------------ Modal starts here ------------------ */}

        <Journal
          open={openJournal}
          onClose={handleCloseJournal}
          calculateFields={calculateFields}
          calculateOutcome={calculateOutcome}
        />
        <Backtests
          open={openNewTest}
          onClose={handleCloseBacktest}
          setAllbackTestSchemas={setAllbackTestSchemas}
          handleArchivingTest={handleArchivingTest}
          editMode={editBacktestMode}
          editBacktest={editBacktestData}
        />
        <NewEntry
          open={open}
          onClose={handleClose}
          calculateFields={calculateFields}
          onOpenForecast={handleOpenForecast}
        />
        <NewForecast
          open={openForecast}
          onClose={handleCloseForecast}
          prefilledData={forecastPrefilledData}
        />

        {/* <PosCal open={openCal} onClose={handleCloseCal} /> */}

        {/* <FXMacroContextPanel
        open={openMacro}
        onClose={handleCloseMacro}
        marketData={{}}
      /> */}

        <ConfirmDialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleRemove}
          title="Delete Trade(s)"
          message={
            toRemove && toRemove.length > 0
              ? `Are you sure you want to delete ${toRemove.length} trade(s)? This action cannot be undone.\n\n${toRemove.map((trade) => `${trade.pair} ${trade.direction} ${trade.entryDate}`).join("\n")}`
              : "Are you sure you want to delete this trade? This action cannot be undone."
          }
        />

        {/* Floating Search Icon */}
        {!equityCurve && (
          <FloatingSearch
            value={quickFilterValue}
            onChange={setQuickFilterValue}
            filteredCount={getFilteredCount()}
            totalCount={getTotalCount()}
            multiFilters={multiFilters}
            onMultiFiltersChange={setMultiFilters}
            logicOperator={logicOperator}
            onLogicOperatorChange={(e, value) =>
              value && setLogicOperator(value)
            }
            starFilters={starFilters}
            onStarFiltersChange={setStarFilters}
          />
        )}
      </div>
    </div>
  );
};

export default ResultsTracker;
